import React, { useEffect, useState } from 'react';

import { Button } from 'src/components/Button';
import { ConfirmModal } from 'src/components/ConfirmModal';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { PERMISSIONS } from 'src/utils/permissions';
import {
  KycRecord,
  formatKycName,
  kycErrorMessage,
  reverifyKyc,
  updateKycRecord,
} from 'src/api/adminKyc';

import {
  ActionRow,
  Card,
  CardTitle,
  CharCount,
  DetailItem,
  DetailLabel,
  DetailValue,
  FieldError,
  MetaLine,
  ReasonInput,
} from './styled';

const REVERIFY_REASON_MAX = 500;

type KycReviewActionsProps = {
  record: KycRecord;
  /** Receives the updated record when the API returned one, else nothing. */
  // eslint-disable-next-line no-unused-vars
  onUpdated: (updated?: KycRecord) => void | Promise<void>;
};

/**
 * Approve / Reject / Re-verify for one record.
 *
 * Rejecting is the only action that gives a customer another attempt, so it
 * stays available on every status except VERIFIED — expired and Veriff-declined
 * records included. The API answers 500 rather than a conflict when an action
 * cannot apply, so the two cases that would hit it are closed off here instead.
 */
const KycReviewActions: React.FC<KycReviewActionsProps> = ({ record, onUpdated }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(String(record.response_message ?? ''));
  const [messageError, setMessageError] = useState('');
  const [reverifyReason, setReverifyReason] = useState('');
  const [confirming, setConfirming] = useState<'approve' | 'reject' | 'reverify' | null>(null);

  useEffect(() => {
    setMessage(String(record.response_message ?? ''));
    setMessageError('');
  }, [record.id, record.response_message]);

  const kycId = record.id;
  const status = String(record.status ?? '').toUpperCase();
  const displayName = formatKycName(record.name) || 'This customer';

  const canApprove = status !== 'VERIFIED';
  const alreadyReopened = status === 'CANCELED' && record.resubmission_allowed === true;
  const canReject = status !== 'VERIFIED' && !alreadyReopened;

  const run = async (
    action: () => Promise<KycRecord | void>,
    successMessage: string,
    failureMessage: string,
  ) => {
    setSaving(true);
    try {
      const updated = await action();
      dispatch(setAppAlert({ message: successMessage, isSuccess: true }));
      await onUpdated((updated as KycRecord) || undefined);
    } catch (e: any) {
      dispatch(setAppAlert({ message: kycErrorMessage(e, failureMessage), isSuccess: false }));
    } finally {
      setSaving(false);
      setConfirming(null);
    }
  };

  const handleApprove = () =>
    run(
      () => updateKycRecord(kycId, { status: 'VERIFIED', response_message: message.trim() || undefined }),
      'KYC approved',
      'Failed to approve KYC',
    );

  const handleReject = () =>
    run(
      () => updateKycRecord(kycId, { status: 'CANCELED', response_message: message.trim() }),
      'KYC rejected — the customer may try again',
      'Failed to reject KYC',
    );

  const handleReverify = () =>
    run(
      async () => {
        await reverifyKyc(kycId, reverifyReason.trim() || undefined);
        setReverifyReason('');
      },
      'A new verification session was opened',
      'Failed to start re-verification',
    );

  // Rejection feedback reaches the customer verbatim, so it cannot be blank.
  const openReject = () => {
    if (!message.trim()) {
      setMessageError('A message is required — the customer sees it in their notification.');
      return;
    }
    setMessageError('');
    setConfirming('reject');
  };

  return (
    <PermissionGuard permission={PERMISSIONS.KYC_REVIEW}>
      <Card>
        <CardTitle>Review</CardTitle>
        <DetailItem>
          <DetailLabel>Message to customer</DetailLabel>
          <ReasonInput
            placeholder="Explain the decision — this is sent to the customer verbatim."
            value={message}
            disabled={saving}
            onChange={(e) => {
              setMessage(e.target.value);
              if (messageError) setMessageError('');
            }}
          />
          {messageError ? <FieldError>{messageError}</FieldError> : null}
        </DetailItem>

        {alreadyReopened ? (
          <MetaLine style={{ marginTop: '0.75rem' }}>
            This record has already been reopened — the customer has another attempt pending, so it
            cannot be rejected again.
          </MetaLine>
        ) : null}

        <ActionRow>
          {canReject ? (
            <Button text="Reject" className="negative" $fullWidth disabled={saving} onClick={openReject} />
          ) : null}
          {canApprove ? (
            <Button
              text="Approve"
              className="positive"
              $fullWidth
              disabled={saving}
              onClick={() => setConfirming('approve')}
            />
          ) : null}
          <Button
            text="Re-verify"
            className="outline"
            $fullWidth
            disabled={saving}
            onClick={() => setConfirming('reverify')}
          />
        </ActionRow>
      </Card>

      {confirming === 'approve' ? (
        <ConfirmModal
          title="Approve this verification?"
          message={`${displayName} will be marked verified and notified that their identity check passed.`}
          confirmText="Approve"
          confirmClassName="positive"
          isLoading={saving}
          onClose={() => setConfirming(null)}
          onConfirm={handleApprove}
        />
      ) : null}

      {confirming === 'reject' ? (
        <ConfirmModal
          title="Reject this verification?"
          message={
            <>
              {displayName} will be notified with your message and given one more attempt.
              <DetailValue style={{ marginTop: '0.75rem' }}>“{message.trim()}”</DetailValue>
            </>
          }
          confirmText="Reject"
          confirmClassName="negative"
          isLoading={saving}
          onClose={() => setConfirming(null)}
          onConfirm={handleReject}
        />
      ) : null}

      {confirming === 'reverify' ? (
        <ConfirmModal
          title="Start a new verification?"
          message={
            <>
              This opens a fresh Veriff session for {displayName} and clears the stored decision.
              {status === 'VERIFIED' ? ' They lose verified access until they pass again.' : ''} Every
              session is billed.
              <DetailItem style={{ marginTop: '0.75rem' }}>
                <DetailLabel>Reason (optional, shown to the customer)</DetailLabel>
                <ReasonInput
                  placeholder="Please verify again with your passport"
                  value={reverifyReason}
                  maxLength={REVERIFY_REASON_MAX}
                  disabled={saving}
                  onChange={(e) => setReverifyReason(e.target.value)}
                />
                <CharCount>
                  {reverifyReason.length}/{REVERIFY_REASON_MAX}
                </CharCount>
              </DetailItem>
            </>
          }
          confirmText="Re-verify"
          isLoading={saving}
          onClose={() => setConfirming(null)}
          onConfirm={handleReverify}
        />
      ) : null}
    </PermissionGuard>
  );
};

export default KycReviewActions;
